import React from 'react';
import { SEOHead } from './components/SEOHead';

export const BlogArticle = ({
  // Header
  category = "[CATEGORY]",
  title = "[Article Title with Main Keyword]",
  hook = "[2-3 sentence hook describing the problem and why it matters to the reader.]",

  // TLDR - AI Search Optimization (appears at very top for quick extraction)
  tldr = "",
  bestFor = "", // e.g., "Newly diagnosed patients", "Caregivers", etc.

  // Meta
  authorName = "Jen Horonjeff, PhD",
  authorTitle = "Co-founder & Chief Patient Officer", // Author's role/title
  authorCredentials = "", // e.g., "Patient health researcher with 15+ years experience"
  authorBio = "[2-4 sentences describing the author's credibility and expertise in this topic.]",
  authorImageUrl = "",
  authorLinkedIn = "", // LinkedIn profile URL for verification
  publishedDate = "2025-01-01",
  updatedDate = "",
  readingTime = "8 min read",

  // Key Takeaways (3-5 bullets)
  takeaway1 = "[Key insight #1 - most important point]",
  takeaway2 = "[Key insight #2]",
  takeaway3 = "[Key insight #3]",
  takeaway4 = "",
  takeaway5 = "",

  // Section 1: What is [topic]?
  section1Title = "What is [topic]?",
  section1Content = "[Simple definition of the topic. 2-3 sentences explaining what it is in plain language.]",
  section1WhyMatters = "[Why this matters to the reader. What problem does understanding this solve?]",

  // Section 2: Who is this for?
  section2Title = "Who is this for?",
  section2Content = "[Describe the audience and their pain points. Be specific about who benefits most.]",

  // Section 3: How it works
  section3Title = "How does it work?",
  step1 = "[Step 1 description]",
  step2 = "[Step 2 description]",
  step3 = "[Step 3 description]",
  step4 = "",
  step5 = "",

  // Section 4: Examples
  section4Title = "Real examples",
  example1Title = "[Example 1 title]",
  example1Content = "[Short story + outcome. Use specific details and data when possible.]",
  example2Title = "[Example 2 title]",
  example2Content = "[Short story + outcome.]",

  // Section 5: Pros and Cons
  section5Title = "Pros, cons, and trade-offs",
  pro1 = "[Pro 1]",
  pro2 = "[Pro 2]",
  pro3 = "",
  con1 = "[Con 1]",
  con2 = "[Con 2]",
  con3 = "",

  // Q&A Section (for LLM extraction)
  qa1Question = "[What is topic?]",
  qa1Answer = "[2-4 sentence direct answer that an AI could quote.]",
  qa2Question = "[How does topic help audience?]",
  qa2Answer = "[Direct answer with specific benefits.]",
  qa3Question = "",
  qa3Answer = "",

  // Glossary terms
  term1 = "",
  term1Definition = "",
  term2 = "",
  term2Definition = "",
  term3 = "",
  term3Definition = "",

  // Comparison table
  showComparison = false,
  comparisonTitle = "How this compares to alternatives",
  option1Name = "[Your approach]",
  option1BestFor = "[Who it's best for]",
  option1Benefits = "[Key benefits]",
  option1Drawbacks = "[Drawbacks]",
  option2Name = "[Alternative A]",
  option2BestFor = "[Who it's best for]",
  option2Benefits = "[Key benefits]",
  option2Drawbacks = "[Drawbacks]",

  // FAQ Section
  faq1Question = "[Frequently asked question 1?]",
  faq1Answer = "[2-5 sentence answer.]",
  faq2Question = "[Frequently asked question 2?]",
  faq2Answer = "[2-5 sentence answer.]",
  faq3Question = "",
  faq3Answer = "",
  faq4Question = "",
  faq4Answer = "",

  // Related Resources
  resource1Title = "",
  resource1Url = "",
  resource2Title = "",
  resource2Url = "",
  resource3Title = "",
  resource3Url = "",

  // Community bridge (contextual Reflect question)
  communityQuestion = null,
  communityAnswers = [],
  onCommunityClick = null,

  // CTA
  ctaHeadline = "Ready to get started?",
  ctaDescription = "[Short description linking the article to action.]",
  ctaButtonText = "Join Real Patients",
  ctaButtonUrl = "https://apply.savvy.coop/register",
  onCtaClick = null,

  // Featured image
  featuredImageUrl = "",
  featuredImageAlt = "",
  featuredImageCaption = "",

  // Page URL for schema
  pageUrl = "https://realpatients.io/articles"
}) => {
  const takeaways = [takeaway1, takeaway2, takeaway3, takeaway4, takeaway5].filter(Boolean);
  const steps = [step1, step2, step3, step4, step5].filter(Boolean);
  const pros = [pro1, pro2, pro3].filter(Boolean);
  const cons = [con1, con2, con3].filter(Boolean);
  const glossaryTerms = [
    { term: term1, definition: term1Definition },
    { term: term2, definition: term2Definition },
    { term: term3, definition: term3Definition }
  ].filter(item => item.term && item.definition);
  const qaItems = [
    { question: qa1Question, answer: qa1Answer },
    { question: qa2Question, answer: qa2Answer },
    { question: qa3Question, answer: qa3Answer }
  ].filter(item => item.question && item.answer);
  const faqItems = [
    { question: faq1Question, answer: faq1Answer },
    { question: faq2Question, answer: faq2Answer },
    { question: faq3Question, answer: faq3Answer },
    { question: faq4Question, answer: faq4Answer }
  ].filter(item => item.question && item.answer);
  const resources = [
    { title: resource1Title, url: resource1Url },
    { title: resource2Title, url: resource2Url },
    { title: resource3Title, url: resource3Url }
  ].filter(item => item.title && item.url);

  // Generate JSON-LD Schema - Enhanced for AI Search
  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": title,
    "description": tldr || hook,
    "author": {
      "@type": "Person",
      "name": authorName,
      "jobTitle": authorTitle,
      "description": authorCredentials || authorBio,
      ...(authorLinkedIn && { "sameAs": authorLinkedIn })
    },
    "publisher": {
      "@type": "Organization",
      "name": "Real Patients",
      "url": "https://realpatients.io"
    },
    "datePublished": publishedDate,
    "dateModified": updatedDate || publishedDate,
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": pageUrl
    },
    ...(bestFor && { "audience": { "@type": "Audience", "audienceType": bestFor } })
  };

  const faqSchema = faqItems.length > 0 ? {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqItems.map(item => ({
      "@type": "Question",
      "name": item.question,
      "acceptedAnswer": { "@type": "Answer", "text": item.answer }
    }))
  } : null;

  // ItemList schema for steps/how-to content
  const howToSchema = steps.length > 0 ? {
    "@context": "https://schema.org",
    "@type": "HowTo",
    "name": section3Title,
    "step": steps.map((step, index) => ({
      "@type": "HowToStep",
      "position": index + 1,
      "text": step
    }))
  } : null;

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  };

  const stepColors = ['#f15b41', '#819cd1', '#fab184', '#385cbc', '#6b8e23'];

  return (
    <>
      {/* Dynamic SEO Meta Tags for Article */}
      <SEOHead
        title={title}
        description={tldr || hook}
        url={pageUrl}
        type="article"
        author={authorName}
        publishedDate={publishedDate}
        modifiedDate={updatedDate || publishedDate}
        section={category}
      />

      <article
        className="blog-post"
        style={{
          fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
          color: '#272726',
          lineHeight: 1.7,
          overflowX: 'hidden'
        }}
      >
      {/* Animated blob keyframes */}
      <style>{`
        @keyframes floatBlob1 {
          0%, 100% { transform: translate(0, 0) scale(1); }
          33% { transform: translate(30px, -20px) scale(1.05); }
          66% { transform: translate(-20px, 20px) scale(0.95); }
        }
        @keyframes floatBlob2 {
          0%, 100% { transform: translate(0, 0) scale(1); }
          33% { transform: translate(-40px, 30px) scale(0.95); }
          66% { transform: translate(20px, -30px) scale(1.05); }
        }
      `}</style>

      {/* Hero Header */}
      <header
        className="post-header"
        style={{
          position: 'relative',
          padding: '100px 20px 80px',
          background: `
            radial-gradient(ellipse 80% 50% at 20% 40%, rgba(129, 156, 209, 0.25) 0%, transparent 50%),
            radial-gradient(ellipse 60% 40% at 80% 20%, rgba(250, 177, 132, 0.2) 0%, transparent 50%),
            radial-gradient(ellipse 70% 50% at 70% 80%, rgba(56, 92, 188, 0.15) 0%, transparent 50%),
            linear-gradient(180deg, #d0c7c7 0%, #e8e4e0 100%)
          `,
          overflow: 'hidden'
        }}
      >
        {/* Floating blobs */}
        <div
          style={{
            position: 'absolute',
            top: '10%',
            left: '5%',
            width: '250px',
            height: '250px',
            borderRadius: '60% 40% 30% 70% / 60% 30% 70% 40%',
            background: 'rgba(129, 156, 209, 0.15)',
            filter: 'blur(40px)',
            animation: 'floatBlob1 20s ease-in-out infinite',
            pointerEvents: 'none'
          }}
        />
        <div
          style={{
            position: 'absolute',
            top: '60%',
            right: '5%',
            width: '200px',
            height: '200px',
            borderRadius: '40% 60% 70% 30% / 40% 50% 60% 50%',
            background: 'rgba(250, 177, 132, 0.2)',
            filter: 'blur(40px)',
            animation: 'floatBlob2 25s ease-in-out infinite',
            pointerEvents: 'none'
          }}
        />

        <div style={{ maxWidth: '800px', margin: '0 auto', position: 'relative', zIndex: 1, textAlign: 'center' }}>
          {/* Category tag */}
          <span
            style={{
              display: 'inline-block',
              backgroundColor: '#f15b41',
              color: 'white',
              fontSize: '11px',
              fontWeight: '700',
              textTransform: 'uppercase',
              letterSpacing: '2px',
              padding: '8px 20px',
              borderRadius: '30px',
              marginBottom: '28px'
            }}
          >
            {category}
          </span>

          {/* Title */}
          <h1
            style={{
              fontFamily: '"Source Serif Pro", Georgia, serif',
              fontSize: 'clamp(36px, 6vw, 56px)',
              fontWeight: '700',
              lineHeight: 1.15,
              marginTop: 0,
              marginBottom: '28px',
              color: '#272726'
            }}
          >
            {title}
          </h1>

          {/* Hook */}
          <p
            style={{
              fontSize: 'clamp(18px, 2.5vw, 22px)',
              color: '#444',
              maxWidth: '680px',
              margin: '0 auto 32px',
              lineHeight: 1.6
            }}
          >
            {hook}
          </p>

          {/* Author & Meta - Enhanced for AI Search */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '16px',
              flexWrap: 'wrap'
            }}
          >
            {authorImageUrl && (
              <img
                src={authorImageUrl}
                alt={authorName}
                style={{
                  width: '56px',
                  height: '56px',
                  borderRadius: '50%',
                  objectFit: 'cover',
                  border: '3px solid white',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                }}
              />
            )}
            <div style={{ textAlign: 'left' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                {authorLinkedIn ? (
                  <a
                    href={authorLinkedIn}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ fontWeight: '600', color: '#272726', fontSize: '15px', textDecoration: 'none' }}
                  >
                    {authorName}
                  </a>
                ) : (
                  <span style={{ fontWeight: '600', color: '#272726', fontSize: '15px' }}>{authorName}</span>
                )}
                {authorLinkedIn && (
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="#0077b5">
                    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                  </svg>
                )}
              </div>
              {authorTitle && (
                <div style={{ fontSize: '13px', color: '#555', marginTop: '2px' }}>{authorTitle}</div>
              )}
              <div style={{ fontSize: '13px', color: '#666', marginTop: '4px' }}>
                {formatDate(publishedDate)}
                {updatedDate && updatedDate !== publishedDate && (
                  <span style={{ color: '#f15b41', fontWeight: '500' }}> · Updated {formatDate(updatedDate)}</span>
                )}
                {' '}· {readingTime}
              </div>
            </div>
          </div>
        </div>

        {/* Wavy bottom */}
        <svg
          style={{
            position: 'absolute',
            bottom: 0,
            left: 0,
            width: '100%',
            height: '60px',
            pointerEvents: 'none'
          }}
          viewBox="0 0 1440 60"
          preserveAspectRatio="none"
        >
          <path
            d="M0,30 C200,60 400,0 600,30 C800,60 1000,10 1200,30 C1350,45 1400,25 1440,35 L1440,60 L0,60 Z"
            fill="#ffffff"
          />
        </svg>
      </header>

      {/* Featured Image */}
      {featuredImageUrl && (
        <figure
          style={{
            maxWidth: '900px',
            margin: '-30px auto 60px',
            padding: '0 20px',
            position: 'relative',
            zIndex: 2
          }}
        >
          <img
            src={featuredImageUrl}
            alt={featuredImageAlt}
            style={{
              width: '100%',
              height: 'auto',
              borderRadius: '20px',
              boxShadow: '0 20px 60px rgba(0,0,0,0.15)'
            }}
          />
          {featuredImageCaption && (
            <figcaption
              style={{
                textAlign: 'center',
                fontSize: '14px',
                color: '#666',
                marginTop: '16px',
                fontStyle: 'italic'
              }}
            >
              {featuredImageCaption}
            </figcaption>
          )}
        </figure>
      )}

      {/* Content wrapper */}
      <div style={{ backgroundColor: '#ffffff' }}>
        {/* TLDR - Quick Summary for AI Search */}
        {tldr && (
          <section
            style={{
              maxWidth: '800px',
              margin: '0 auto 40px',
              padding: '0 20px'
            }}
          >
            <div
              style={{
                background: 'linear-gradient(135deg, #385cbc 0%, #2d4a96 100%)',
                borderRadius: '16px',
                padding: '24px 28px',
                color: 'white'
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
                <span style={{
                  backgroundColor: 'rgba(255,255,255,0.2)',
                  padding: '4px 12px',
                  borderRadius: '20px',
                  fontSize: '12px',
                  fontWeight: '700',
                  letterSpacing: '1px'
                }}>
                  TLDR
                </span>
                {bestFor && (
                  <span style={{
                    backgroundColor: 'rgba(241, 91, 65, 0.9)',
                    padding: '4px 12px',
                    borderRadius: '20px',
                    fontSize: '12px',
                    fontWeight: '600'
                  }}>
                    Best for: {bestFor}
                  </span>
                )}
              </div>
              <p style={{
                margin: 0,
                fontSize: '17px',
                lineHeight: 1.6,
                fontWeight: '500'
              }}>
                {tldr}
              </p>
            </div>
          </section>
        )}

        {/* Key Takeaways - Highlight Box */}
        <section
          style={{
            maxWidth: '800px',
            margin: '0 auto 60px',
            padding: '0 20px'
          }}
        >
          <div
            style={{
              background: 'linear-gradient(135deg, #fff8e7 0%, #fff1d4 100%)',
              borderRadius: '20px',
              padding: '32px 36px',
              position: 'relative',
              overflow: 'hidden',
              textAlign: 'left'
            }}
          >
            {/* Decorative corner accent */}
            <div
              style={{
                position: 'absolute',
                top: 0,
                right: 0,
                width: '120px',
                height: '120px',
                background: 'linear-gradient(135deg, rgba(241, 91, 65, 0.15) 0%, transparent 60%)',
                borderRadius: '0 20px 0 100%'
              }}
            />
            <h2
              style={{
                fontFamily: '"Source Serif Pro", Georgia, serif',
                fontSize: '22px',
                fontWeight: '700',
                marginTop: 0,
                marginBottom: '20px',
                color: '#272726',
                display: 'flex',
                alignItems: 'center',
                gap: '12px'
              }}
            >
              <span style={{ fontSize: '28px' }}>✨</span>
              Key Takeaways
            </h2>
            <ul
              style={{
                margin: 0,
                padding: 0,
                listStyle: 'none',
                textAlign: 'left'
              }}
            >
              {takeaways.map((takeaway, index) => (
                <li
                  key={index}
                  style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '14px',
                    marginBottom: index < takeaways.length - 1 ? '14px' : 0,
                    fontSize: '16px',
                    lineHeight: 1.6,
                    textAlign: 'left'
                  }}
                >
                  <span
                    style={{
                      width: '24px',
                      height: '24px',
                      borderRadius: '50%',
                      backgroundColor: '#f15b41',
                      color: 'white',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '12px',
                      fontWeight: '700',
                      flexShrink: 0,
                      marginTop: '2px'
                    }}
                  >
                    {index + 1}
                  </span>
                  {takeaway}
                </li>
              ))}
            </ul>
          </div>
        </section>

        {/* Table of Contents */}
        <nav
          style={{
            maxWidth: '800px',
            margin: '0 auto 60px',
            padding: '0 20px'
          }}
        >
          <div
            style={{
              backgroundColor: '#f7f5f3',
              borderRadius: '16px',
              padding: '28px 32px',
              border: '1px solid #e8e4e0',
              textAlign: 'left'
            }}
          >
            <h2
              style={{
                fontFamily: '"Source Serif Pro", Georgia, serif',
                fontSize: '18px',
                fontWeight: '600',
                marginTop: 0,
                marginBottom: '16px',
                color: '#272726',
                textAlign: 'left'
              }}
            >
              In this article
            </h2>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                gap: '10px',
                textAlign: 'left'
              }}
            >
              {[
                { href: '#what-is', text: section1Title },
                { href: '#who-is-this-for', text: section2Title },
                { href: '#how-it-works', text: section3Title },
                { href: '#examples', text: section4Title },
                { href: '#pros-cons', text: section5Title },
                ...(faqItems.length > 0 ? [{ href: '#faq', text: 'FAQs' }] : [])
              ].map((item, index) => (
                <a
                  key={index}
                  href={item.href}
                  style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '10px',
                    color: '#272726',
                    textDecoration: 'none',
                    fontSize: '15px',
                    padding: '8px 12px',
                    borderRadius: '8px',
                    transition: 'background-color 0.2s ease',
                    textAlign: 'left'
                  }}
                >
                  <span style={{ color: '#f15b41', fontWeight: '600' }}>{index + 1}.</span>
                  {item.text}
                </a>
              ))}
            </div>
          </div>
        </nav>

        {/* Main Content */}
        <section style={{ maxWidth: '800px', margin: '0 auto', padding: '0 20px', textAlign: 'left' }}>

          {/* Section 1: What is [topic]? */}
          <section id="what-is" style={{ marginBottom: '64px' }}>
            <h2
              style={{
                fontFamily: '"Source Serif Pro", Georgia, serif',
                fontSize: 'clamp(28px, 4vw, 36px)',
                fontWeight: '700',
                marginTop: 0,
                marginBottom: '24px',
                color: '#272726',
                position: 'relative',
                paddingLeft: '20px',
                borderLeft: '4px solid #f15b41'
              }}
            >
              {section1Title}
            </h2>
            <p style={{ fontSize: '18px', marginBottom: '20px', color: '#333', textAlign: 'left' }}>{section1Content}</p>
            <p style={{ fontSize: '18px', marginBottom: 0, color: '#333', textAlign: 'left' }}>{section1WhyMatters}</p>
          </section>

          {/* Section 2: Who is this for? */}
          <section id="who-is-this-for" style={{ marginBottom: '64px' }}>
            <h2
              style={{
                fontFamily: '"Source Serif Pro", Georgia, serif',
                fontSize: 'clamp(28px, 4vw, 36px)',
                fontWeight: '700',
                marginTop: 0,
                marginBottom: '24px',
                color: '#272726',
                position: 'relative',
                paddingLeft: '20px',
                borderLeft: '4px solid #819cd1'
              }}
            >
              {section2Title}
            </h2>
            <p style={{ fontSize: '18px', marginBottom: 0, color: '#333', textAlign: 'left' }}>{section2Content}</p>
          </section>

          {/* Section 3: How it works - Visual Steps */}
          <section id="how-it-works" style={{ marginBottom: '64px' }}>
            <h2
              style={{
                fontFamily: '"Source Serif Pro", Georgia, serif',
                fontSize: 'clamp(28px, 4vw, 36px)',
                fontWeight: '700',
                marginTop: 0,
                marginBottom: '32px',
                color: '#272726',
                position: 'relative',
                paddingLeft: '20px',
                borderLeft: '4px solid #fab184'
              }}
            >
              {section3Title}
            </h2>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
              {steps.map((step, index) => (
                <div
                  key={index}
                  style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '20px',
                    padding: '24px',
                    backgroundColor: '#f7f5f3',
                    borderRadius: '16px',
                    position: 'relative'
                  }}
                >
                  <div
                    style={{
                      width: '48px',
                      height: '48px',
                      borderRadius: '50%',
                      backgroundColor: stepColors[index % stepColors.length],
                      color: 'white',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontFamily: '"Source Serif Pro", Georgia, serif',
                      fontSize: '20px',
                      fontWeight: '700',
                      flexShrink: 0
                    }}
                  >
                    {index + 1}
                  </div>
                  <div style={{ flex: 1, textAlign: 'left' }}>
                    <p style={{ fontSize: '17px', margin: 0, color: '#333', lineHeight: 1.6, textAlign: 'left' }}>{step}</p>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* Section 4: Examples - Cards */}
          <section id="examples" style={{ marginBottom: '64px' }}>
            <h2
              style={{
                fontFamily: '"Source Serif Pro", Georgia, serif',
                fontSize: 'clamp(28px, 4vw, 36px)',
                fontWeight: '700',
                marginTop: 0,
                marginBottom: '32px',
                color: '#272726',
                position: 'relative',
                paddingLeft: '20px',
                borderLeft: '4px solid #385cbc'
              }}
            >
              {section4Title}
            </h2>
            <div style={{ display: 'grid', gap: '24px' }}>
              <div
                style={{
                  background: 'linear-gradient(135deg, #f0f4ff 0%, #e8efff 100%)',
                  padding: '32px',
                  borderRadius: '20px',
                  position: 'relative',
                  overflow: 'hidden',
                  textAlign: 'left'
                }}
              >
                <div
                  style={{
                    position: 'absolute',
                    top: '-20px',
                    right: '-20px',
                    width: '100px',
                    height: '100px',
                    borderRadius: '50%',
                    background: 'rgba(129, 156, 209, 0.2)',
                    filter: 'blur(20px)'
                  }}
                />
                <h3
                  style={{
                    fontFamily: '"Source Serif Pro", Georgia, serif',
                    fontSize: '22px',
                    fontWeight: '600',
                    marginTop: 0,
                    marginBottom: '16px',
                    color: '#272726'
                  }}
                >
                  {example1Title}
                </h3>
                <p style={{ fontSize: '16px', marginBottom: 0, color: '#444', lineHeight: 1.7, textAlign: 'left' }}>{example1Content}</p>
              </div>

              <div
                style={{
                  background: 'linear-gradient(135deg, #fff5f0 0%, #ffe8e0 100%)',
                  padding: '32px',
                  borderRadius: '20px',
                  position: 'relative',
                  overflow: 'hidden',
                  textAlign: 'left'
                }}
              >
                <div
                  style={{
                    position: 'absolute',
                    top: '-20px',
                    right: '-20px',
                    width: '100px',
                    height: '100px',
                    borderRadius: '50%',
                    background: 'rgba(250, 177, 132, 0.3)',
                    filter: 'blur(20px)'
                  }}
                />
                <h3
                  style={{
                    fontFamily: '"Source Serif Pro", Georgia, serif',
                    fontSize: '22px',
                    fontWeight: '600',
                    marginTop: 0,
                    marginBottom: '16px',
                    color: '#272726'
                  }}
                >
                  {example2Title}
                </h3>
                <p style={{ fontSize: '16px', marginBottom: 0, color: '#444', lineHeight: 1.7, textAlign: 'left' }}>{example2Content}</p>
              </div>
            </div>
          </section>

          {/* Section 5: Pros and Cons */}
          <section id="pros-cons" style={{ marginBottom: '64px' }}>
            <h2
              style={{
                fontFamily: '"Source Serif Pro", Georgia, serif',
                fontSize: 'clamp(28px, 4vw, 36px)',
                fontWeight: '700',
                marginTop: 0,
                marginBottom: '32px',
                color: '#272726',
                position: 'relative',
                paddingLeft: '20px',
                borderLeft: '4px solid #6b8e23'
              }}
            >
              {section5Title}
            </h2>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
                gap: '24px'
              }}
            >
              {/* Pros */}
              <div
                style={{
                  background: 'linear-gradient(180deg, #e8f5e9 0%, #c8e6c9 100%)',
                  padding: '28px',
                  borderRadius: '20px'
                }}
              >
                <h3
                  style={{
                    fontSize: '18px',
                    fontWeight: '700',
                    marginTop: 0,
                    marginBottom: '20px',
                    color: '#2e7d32',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px'
                  }}
                >
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                    <circle cx="12" cy="12" r="11" stroke="#2e7d32" strokeWidth="2"/>
                    <path d="M7 12L10 15L17 9" stroke="#2e7d32" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  Pros
                </h3>
                <ul style={{ margin: 0, padding: 0, listStyle: 'none', textAlign: 'left' }}>
                  {pros.map((pro, index) => (
                    <li
                      key={index}
                      style={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: '12px',
                        marginBottom: index < pros.length - 1 ? '12px' : 0,
                        fontSize: '15px',
                        color: '#1b5e20',
                        textAlign: 'left'
                      }}
                    >
                      <span style={{ color: '#2e7d32', fontWeight: '700' }}>+</span>
                      {pro}
                    </li>
                  ))}
                </ul>
              </div>

              {/* Cons */}
              <div
                style={{
                  background: 'linear-gradient(180deg, #ffebee 0%, #ffcdd2 100%)',
                  padding: '28px',
                  borderRadius: '20px'
                }}
              >
                <h3
                  style={{
                    fontSize: '18px',
                    fontWeight: '700',
                    marginTop: 0,
                    marginBottom: '20px',
                    color: '#c62828',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px'
                  }}
                >
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                    <circle cx="12" cy="12" r="11" stroke="#c62828" strokeWidth="2"/>
                    <path d="M8 8L16 16M16 8L8 16" stroke="#c62828" strokeWidth="2" strokeLinecap="round"/>
                  </svg>
                  Cons
                </h3>
                <ul style={{ margin: 0, padding: 0, listStyle: 'none', textAlign: 'left' }}>
                  {cons.map((con, index) => (
                    <li
                      key={index}
                      style={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: '12px',
                        marginBottom: index < cons.length - 1 ? '12px' : 0,
                        fontSize: '15px',
                        color: '#b71c1c',
                        textAlign: 'left'
                      }}
                    >
                      <span style={{ color: '#c62828', fontWeight: '700' }}>−</span>
                      {con}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </section>
        </section>

        {/* Q&A Section for LLM extraction */}
        {qaItems.length > 0 && (
          <section
            style={{
              backgroundColor: '#f7f5f3',
              padding: '60px 20px',
              marginBottom: '0'
            }}
          >
            <div style={{ maxWidth: '800px', margin: '0 auto', textAlign: 'left' }}>
              <h2
                style={{
                  fontFamily: '"Source Serif Pro", Georgia, serif',
                  fontSize: 'clamp(28px, 4vw, 36px)',
                  fontWeight: '700',
                  marginTop: 0,
                  marginBottom: '32px',
                  color: '#272726',
                  textAlign: 'left'
                }}
              >
                Quick Answers
              </h2>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '20px', textAlign: 'left' }}>
                {qaItems.map((item, index) => (
                  <article
                    key={index}
                    style={{
                      backgroundColor: 'white',
                      padding: '28px 32px',
                      borderRadius: '16px',
                      boxShadow: '0 4px 20px rgba(0,0,0,0.06)',
                      textAlign: 'left'
                    }}
                  >
                    <h3
                      style={{
                        fontSize: '18px',
                        fontWeight: '600',
                        marginTop: 0,
                        marginBottom: '12px',
                        color: '#f15b41',
                        textAlign: 'left'
                      }}
                    >
                      Q: {item.question}
                    </h3>
                    <p style={{ fontSize: '16px', marginBottom: 0, color: '#444', lineHeight: 1.7, textAlign: 'left' }}>
                      {item.answer}
                    </p>
                  </article>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* Glossary */}
        {glossaryTerms.length > 0 && (
          <section style={{ maxWidth: '800px', margin: '60px auto', padding: '0 20px', textAlign: 'left' }}>
            <h2
              style={{
                fontFamily: '"Source Serif Pro", Georgia, serif',
                fontSize: '24px',
                fontWeight: '700',
                marginTop: 0,
                marginBottom: '24px',
                color: '#272726',
                textAlign: 'left'
              }}
            >
              Key Terms
            </h2>
            <dl style={{ margin: 0, textAlign: 'left' }}>
              {glossaryTerms.map((item, index) => (
                <div
                  key={index}
                  style={{
                    padding: '20px 24px',
                    backgroundColor: index % 2 === 0 ? '#f7f5f3' : 'white',
                    borderRadius: '12px',
                    marginBottom: '12px',
                    textAlign: 'left'
                  }}
                >
                  <dt style={{ fontWeight: '700', fontSize: '17px', marginBottom: '6px', color: '#272726', textAlign: 'left' }}>{item.term}</dt>
                  <dd style={{ margin: 0, color: '#555', fontSize: '16px', textAlign: 'left' }}>{item.definition}</dd>
                </div>
              ))}
            </dl>
          </section>
        )}

        {/* FAQ Section */}
        {faqItems.length > 0 && (
          <section
            id="faq"
            style={{
              maxWidth: '800px',
              margin: '0 auto 60px',
              padding: '0 20px',
              textAlign: 'left'
            }}
          >
            <h2
              style={{
                fontFamily: '"Source Serif Pro", Georgia, serif',
                fontSize: 'clamp(28px, 4vw, 36px)',
                fontWeight: '700',
                marginTop: 0,
                marginBottom: '32px',
                color: '#272726',
                textAlign: 'left'
              }}
            >
              Frequently Asked Questions
            </h2>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', textAlign: 'left' }}>
              {faqItems.map((item, index) => (
                <article
                  key={index}
                  style={{
                    padding: '24px 28px',
                    backgroundColor: '#f7f5f3',
                    borderRadius: '16px',
                    borderLeft: '4px solid #819cd1',
                    textAlign: 'left'
                  }}
                >
                  <h3
                    style={{
                      fontSize: '18px',
                      fontWeight: '600',
                      marginTop: 0,
                      marginBottom: '12px',
                      color: '#272726',
                      textAlign: 'left'
                    }}
                  >
                    {item.question}
                  </h3>
                  <p style={{ fontSize: '16px', marginBottom: 0, color: '#444', lineHeight: 1.7, textAlign: 'left' }}>
                    {item.answer}
                  </p>
                </article>
              ))}
            </div>
          </section>
        )}

        {/* Related Resources */}
        {resources.length > 0 && (
          <section style={{ maxWidth: '800px', margin: '0 auto 60px', padding: '0 20px', textAlign: 'left' }}>
            <h2
              style={{
                fontFamily: '"Source Serif Pro", Georgia, serif',
                fontSize: '22px',
                fontWeight: '600',
                marginTop: 0,
                marginBottom: '20px',
                textAlign: 'left'
              }}
            >
              Related Resources
            </h2>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {resources.map((resource, index) => (
                <a
                  key={index}
                  href={resource.url}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    padding: '16px 20px',
                    backgroundColor: '#f7f5f3',
                    borderRadius: '12px',
                    color: '#272726',
                    textDecoration: 'none',
                    fontSize: '16px',
                    fontWeight: '500',
                    transition: 'background-color 0.2s ease'
                  }}
                >
                  <span style={{ color: '#f15b41' }}>→</span>
                  {resource.title}
                </a>
              ))}
            </div>
          </section>
        )}
      </div>

      {/* Community Bridge — contextual Reflect question */}
      {communityQuestion && communityAnswers.length > 0 && onCommunityClick && (
        <section style={{ backgroundColor: '#f7f5f3', padding: '60px 20px' }}>
          <div style={{ maxWidth: '700px', margin: '0 auto' }}>
            <p style={{
              fontSize: '12px', fontWeight: '700', textTransform: 'uppercase',
              letterSpacing: '1px', color: '#EBA09B', margin: '0 0 12px'
            }}>
              From the community
            </p>
            <h3 style={{
              fontFamily: '"Source Serif Pro", Georgia, serif',
              fontSize: 'clamp(22px, 3vw, 28px)', fontWeight: '700',
              color: '#272726', margin: '0 0 24px', lineHeight: 1.3
            }}>
              {communityQuestion}
            </h3>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', marginBottom: '24px' }}>
              {communityAnswers.slice(0, 2).map((answer) => (
                <div key={answer.id} style={{
                  backgroundColor: '#fff', borderRadius: '12px', padding: '20px',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.04)', borderLeft: '3px solid #EBA09B'
                }}>
                  <p style={{
                    fontSize: '15px', lineHeight: 1.6, color: '#333', margin: '0 0 12px',
                    fontStyle: 'italic'
                  }}>
                    "{answer.text}"
                  </p>
                  <div style={{
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                    fontSize: '13px', color: '#888'
                  }}>
                    <span>{answer.author} · {answer.diagnosisTime}</span>
                    <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
                        stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                        <path d="M12 19V5M5 12l7-7 7 7" />
                      </svg>
                      {answer.votes}
                    </span>
                  </div>
                </div>
              ))}
            </div>

            <p style={{ fontSize: '14px', color: '#666', margin: '0 0 20px' }}>
              {communityAnswers.length - 2 > 0
                ? `${communityAnswers.length - 2} more responses`
                : 'Be one of the first to share your experience.'}
            </p>

            <button
              onClick={onCommunityClick}
              style={{
                backgroundColor: '#272726', color: '#fff', border: 'none',
                borderRadius: '6px', padding: '14px 28px', fontSize: '15px',
                fontWeight: '600', cursor: 'pointer', transition: 'background-color 0.2s'
              }}
              onMouseEnter={(e) => e.target.style.backgroundColor = '#3a3a39'}
              onMouseLeave={(e) => e.target.style.backgroundColor = '#272726'}
            >
              Add Your Voice
            </button>
          </div>
        </section>
      )}

      {/* CTA Section */}
      <section
        style={{
          position: 'relative',
          padding: '80px 20px',
          background: `
            radial-gradient(ellipse 60% 50% at 30% 50%, rgba(241, 91, 65, 0.15) 0%, transparent 50%),
            radial-gradient(ellipse 50% 50% at 70% 50%, rgba(250, 177, 132, 0.2) 0%, transparent 50%),
            linear-gradient(180deg, #fff5f0 0%, #ffe8e0 100%)
          `,
          overflow: 'hidden'
        }}
      >
        {/* Wavy top */}
        <svg
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '50px',
            pointerEvents: 'none'
          }}
          viewBox="0 0 1440 50"
          preserveAspectRatio="none"
        >
          <path
            d="M0,25 C300,50 600,0 900,25 C1100,45 1300,10 1440,30 L1440,0 L0,0 Z"
            fill="#ffffff"
          />
        </svg>

        <div style={{ maxWidth: '700px', margin: '0 auto', textAlign: 'center', position: 'relative', zIndex: 1 }}>
          <h2
            style={{
              fontFamily: '"Source Serif Pro", Georgia, serif',
              fontSize: 'clamp(32px, 5vw, 44px)',
              fontWeight: '700',
              marginTop: 0,
              marginBottom: '20px',
              color: '#272726'
            }}
          >
            {ctaHeadline}
          </h2>
          <p
            style={{
              fontSize: '18px',
              marginBottom: '32px',
              color: '#444',
              maxWidth: '500px',
              margin: '0 auto 32px'
            }}
          >
            {ctaDescription}
          </p>
          {onCtaClick ? (
            <button
              onClick={onCtaClick}
              style={{
                display: 'inline-block',
                backgroundColor: '#f15b41',
                color: 'white',
                fontSize: '16px',
                fontWeight: '700',
                textTransform: 'uppercase',
                letterSpacing: '1px',
                padding: '18px 48px',
                border: '2px solid #f15b41',
                borderRadius: '0',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                fontFamily: 'inherit'
              }}
            >
              {ctaButtonText}
            </button>
          ) : (
            <a
              href={ctaButtonUrl}
              style={{
                display: 'inline-block',
                backgroundColor: '#f15b41',
                color: 'white',
                fontSize: '16px',
                fontWeight: '700',
                textTransform: 'uppercase',
                letterSpacing: '1px',
                textDecoration: 'none',
                padding: '18px 48px',
                border: '2px solid #f15b41',
                borderRadius: '0',
                cursor: 'pointer',
                transition: 'all 0.2s ease'
              }}
            >
              {ctaButtonText}
            </a>
          )}
        </div>
      </section>

      {/* Author Bio */}
      <section
        style={{
          backgroundColor: '#f7f5f3',
          padding: '60px 20px'
        }}
      >
        <div
          style={{
            maxWidth: '700px',
            margin: '0 auto',
            display: 'flex',
            alignItems: 'flex-start',
            gap: '24px',
            flexWrap: 'wrap'
          }}
        >
          {authorImageUrl && (
            <img
              src={authorImageUrl}
              alt={authorName}
              style={{
                width: '80px',
                height: '80px',
                borderRadius: '50%',
                objectFit: 'cover',
                border: '4px solid white',
                boxShadow: '0 4px 16px rgba(0,0,0,0.1)'
              }}
            />
          )}
          <div style={{ flex: 1, minWidth: '250px' }}>
            <p style={{ fontSize: '12px', textTransform: 'uppercase', letterSpacing: '2px', color: '#666', marginTop: 0, marginBottom: '8px' }}>
              Written by
            </p>
            <h3
              style={{
                fontFamily: '"Source Serif Pro", Georgia, serif',
                fontSize: '22px',
                fontWeight: '700',
                marginTop: 0,
                marginBottom: '12px',
                color: '#272726'
              }}
            >
              {authorName}
            </h3>
            <p style={{ fontSize: '16px', marginBottom: 0, color: '#555', lineHeight: 1.6 }}>
              {authorBio}
            </p>
          </div>
        </div>
      </section>

      {/* Footer meta */}
      <footer
        style={{
          padding: '32px 20px',
          borderTop: '1px solid #e8e4e0',
          textAlign: 'center',
          fontSize: '14px',
          color: '#666',
          backgroundColor: '#ffffff'
        }}
      >
        <p style={{ marginTop: 0, marginBottom: '8px' }}>
          Published {formatDate(publishedDate)}
          {updatedDate && updatedDate !== publishedDate && ` · Updated ${formatDate(updatedDate)}`}
        </p>
      </footer>

      {/* JSON-LD Schema */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(articleSchema) }}
      />
      {faqSchema && (
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
        />
      )}
      {howToSchema && (
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(howToSchema) }}
        />
      )}
    </article>
    </>
  );
};
